import React, { useEffect } from 'react';
import PricingSection from '../../components/Home/PricingSection';

const CANONICAL = 'https://explanatum.co.uk/modules/linkedin-enhancer';

const LinkedInEnhancerLanding: React.FC = () => {
  useEffect(() => {
    const title = 'AI LinkedIn Enhancer | Explanatum';
    const desc = 'Optimize personal and company LinkedIn profiles in minutes. Generate headlines, summaries, service offerings, and outreach-ready content from a single workspace.';

    document.title = title;

    const setMeta = (name: string, content: string) => {
      let el = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('name', name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    const setOg = (property: string, content: string) => {
      let el = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('property', property);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    setMeta('description', desc);
    setOg('og:title', title);
    setOg('og:description', desc);
    setOg('og:url', CANONICAL);

    let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!link) {
      link = document.createElement('link');
      link.setAttribute('rel', 'canonical');
      document.head.appendChild(link);
    }
    link.setAttribute('href', CANONICAL);
  }, []);

  const track = (event: string, params: Record<string, unknown> = {}) => {
    try {
      const w = window as unknown as { gtag?: (...args: unknown[]) => void };
      if (w && typeof w.gtag === 'function') w.gtag('event', event, params);
    } catch {}
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: 'Can this enhance both personal and company LinkedIn profiles?',
        acceptedAnswer: {
          '@type': 'Answer',
          text:
            'Yes. Explanatum combines the personal profile enhancer and company page optimizer so founders can align their personal brand with the business narrative in one workflow.',
        },
      },
      {
        '@type': 'Question',
        name: 'What content does the AI generate?',
        acceptedAnswer: {
          '@type': 'Answer',
          text:
            'Professional headlines, summaries, experience blurbs, service descriptions, value propositions, calls to action, and recommended hashtags.',
        },
      },
      {
        '@type': 'Question',
        name: 'How fast are the outputs?',
        acceptedAnswer: {
          '@type': 'Answer',
          text:
            'You receive polished drafts in seconds. That means profile refreshes in minutes versus days waiting on consultants or Fiverr contractors.',
        },
      },
      {
        '@type': 'Question',
        name: 'Can I collaborate with my team?',
        acceptedAnswer: {
          '@type': 'Answer',
          text:
            'Share drafts inside Explanatum, gather feedback, and let the AI Business Consultant push approved updates into outreach sequences or CRM notes.',
        },
      },
      {
        '@type': 'Question',
        name: 'How does it compare to hiring a human consultant?',
        acceptedAnswer: {
          '@type': 'Answer',
          text:
            'Consultants can take a week and cost hundreds per engagement. Explanatum delivers immediately, costs less, and keeps the content connected to your marketing workflows.',
        },
      },
    ],
  } as const;

  return (
    <main className="bg-white" role="main">
      {/* Hero */}
      <section aria-label="LinkedIn enhancer overview" className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 pt-20 pb-24 lg:pt-28 lg:pb-32 relative">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <div>
              <span className="inline-flex items-center gap-2 rounded-full border border-sky-400/40 bg-sky-500/10 px-4 py-1 text-sm font-semibold text-sky-700">
                <span className="inline-flex h-2 w-2 rounded-full bg-sky-300" />
                AI LinkedIn Enhancer
              </span>
              <h1 className="mt-6 text-4xl font-extrabold tracking-tight text-gray-900 sm:text-5xl lg:text-6xl">
                Align your founder profile and company page in minutes.
                <span className="mt-2 block text-blue-600">
                  Every headline, summary, and call-to-action handled.
                </span>
              </h1>
              <p className="mt-6 text-lg text-gray-600 max-w-xl">
                Switch between personal and business prompts without opening new tools. Explanatum crafts polished LinkedIn profiles, aligns messaging, and feeds winning copy into your outreach sequences instantly.
              </p>
              <div className="mt-8 flex flex-wrap items-center gap-4">
                <a
                  href="#pricing"
                  onClick={() => track('click_linkedin_cta', { placement: 'hero' })}
                  className="inline-flex items-center justify-center rounded-full bg-gradient-to-r from-sky-500 via-indigo-500 to-cyan-400 px-6 py-3 text-base font-semibold text-white shadow-lg shadow-sky-500/30 transition hover:scale-[1.02]"
                >
                  Start 7-day trial
                </a>
                <a
                  href="/marketing/linkedin-profile-enhancer"
                  className="inline-flex items-center justify-center rounded-full border border-blue-600/50 px-6 py-3 text-base font-semibold text-gray-600 hover:bg-blue-50 transition-colors"
                >
                  Enhance a profile now
                </a>
              </div>
              <div className="mt-8 flex flex-wrap items-center gap-4 text-sm text-gray-500">
                <span className="flex items-center gap-2">
                  <span className="inline-flex h-2 w-2 rounded-full bg-emerald-400" />
                  Personal + company pages in one flow
                </span>
                <span className="flex items-center gap-2">
                  <span className="inline-flex h-2 w-2 rounded-full bg-sky-400" />
                  High-impact drafts in seconds
                </span>
                <span className="flex items-center gap-2">
                  <span className="inline-flex h-2 w-2 rounded-full bg-blue-600" />
                  Integrated with AI Business Consultant
                </span>
              </div>
            </div>
            <aside className="relative" aria-label="Preview of outputs">
              <div className="absolute -top-6 -left-6 h-32 w-32 rounded-full bg-blue-50 blur-3xl" aria-hidden="true" />
              <div className="absolute -bottom-10 -right-8 h-36 w-36 rounded-full bg-cyan-50 blur-3xl" aria-hidden="true" />
              <div className="relative rounded-3xl border border-gray-200 bg-white p-6 shadow-2xl ">
                <header className="flex items-center justify-between text-xs uppercase tracking-widest text-gray-500">
                  <span>Founder profile</span>
                  <span>Company page</span>
                </header>
                <div className="mt-5 space-y-5">
                  <article className="rounded-2xl border border-gray-200 bg-white p-4">
                    <p className="text-[13px] text-gray-600">Headline: “Product-led GTM leader | Scaling SaaS brands from £1m-£10m ARR | Speaker & AI marketer.”</p>
                    <p className="mt-3 text-[12px] text-gray-500">Summary, achievements, and CTA drafted automatically.</p>
                  </article>
                  <article className="rounded-2xl border border-gray-200 bg-white p-4">
                    <p className="text-[13px] text-gray-600">Company overview, services, and proof points ready to paste to your LinkedIn page and export to outreach cadences.</p>
                  </article>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section aria-label="Benefits" className="bg-white py-20">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center">
            <h2 className="text-3xl font-bold text-gray-900 sm:text-4xl">Your narrative, consistent across every LinkedIn touchpoint</h2>
            <p className="mt-4 text-base text-gray-500">Automate the writing so you can stay focused on engaging prospects and partners.</p>
          </div>
          <div className="mt-14 grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            {[{
              title: 'Founder + company alignment',
              body: 'Match your personal brand to your business storyline with unified messaging and CTAs.'
            }, {
              title: 'AI-graded best practices',
              body: 'Headlines, summaries, and service descriptions follow LinkedIn SEO and conversion heuristics automatically.'
            }, {
              title: 'Outreach ready',
              body: 'Push approved copy to the AI Business Consultant for follow-up messages, InMail drafts, and nurture cadences.'
            }, {
              title: 'Recruiting support',
              body: 'Generate role descriptions and team highlights that attract talent straight from your company page.'
            }, {
              title: 'Investor credibility',
              body: 'Show traction, metrics, and proof faster when raising capital or warming strategic partners.'
            }, {
              title: 'Continuous refresh',
              body: 'Iterate in minutes each time you launch a product, campaign, or new positioning.'
            }].map((item) => (
              <article key={item.title} className="rounded-3xl border border-gray-200 bg-white p-6 shadow-lg shadow-lg">
                <h3 className="text-xl font-semibold text-gray-900">{item.title}</h3>
                <p className="mt-3 text-sm text-gray-500">{item.body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Comparison */}
      <section id="compare" aria-label="Comparison" className="relative overflow-hidden bg-white py-20">
        <div className="max-w-6xl mx-auto px-6 relative">
          <div className="text-center">
            <h2 className="text-3xl font-bold text-gray-900">Why founders upgrade from human consultants and DIY</h2>
            <p className="mt-4 text-base text-gray-500">Stop paying £300+ per profile refresh or spending weekends polishing copy by hand.</p>
          </div>
          <div className="mt-14 grid gap-6 md:grid-cols-3">
            {[{
              title: 'Explanatum AI LinkedIn Enhancer',
              points: [
                'Unlimited profile refreshes included with your plan',
                'Personal + company workflows in a single session',
                'Drafts ready in seconds—no scheduling or revision delays',
                'Routes approved copy to consultants, CRM, and outreach tools',
                'Maintains consistent messaging across fundraising, sales, and hiring'
              ],
              accent: 'border-gray-200 bg-gray-50 text-white'
            }, {
              title: 'Human consultants',
              points: [
                'Engagement fees £300–£800 per profile update',
                'Project timelines span 5–10 days including interviews',
                'Revisions require additional calls and added cost',
                'Outputs arrive via email—no integration with your stack',
                'Difficult to iterate frequently for campaigns or pivots'
              ],
              accent: 'border-gray-200 bg-gray-50 text-white'
            }, {
              title: 'DIY best-effort updates',
              points: [
                'Lots of time lost researching formats and keywords',
                'Inconsistent tone between personal and business pages',
                'Easy to miss proof points that drive conversions',
                'No automated follow-up tasks or outreach drafts',
                'Quality depends on whoever has time to write internally'
              ],
              accent: 'border-gray-200 bg-gray-50 text-white'
            }].map((column) => (
              <article key={column.title} className={`rounded-3xl border ${column.accent} p-6 shadow-xl shadow-lg`}>
                <h3 className="text-lg font-semibold text-gray-900">{column.title}</h3>
                <ul className="mt-5 space-y-3 text-sm text-gray-900">
                  {column.points.map((point) => (
                    <li key={point} className="flex items-start gap-3 text-gray-900">
                      <span className="mt-1 inline-flex h-1.5 w-1.5 rounded-full bg-white" />
                      <span className="text-gray-900">{point}</span>
                    </li>
                  ))}
                </ul>
              </article>
            ))}
          </div>
          <div className="mt-14 rounded-3xl border border-gray-200 bg-sky-400/10 p-8 text-center ">
            <h3 className="text-2xl font-semibold text-gray-900">“Can we rework the team’s profiles before investor meetings next week?”</h3>
            <p className="mt-3 text-base text-gray-600">Generate founder bios, company positioning, and hiring callouts in one sitting—then let the AI Business Consultant follow through.</p>
            <div className="mt-6 flex flex-wrap justify-center gap-4">
              <a
                href="#pricing"
                onClick={() => track('click_linkedin_cta', { placement: 'compare' })}
                className="inline-flex items-center justify-center rounded-full bg-gradient-to-r from-sky-500 via-indigo-500 to-blue-600 px-6 py-3 text-base font-semibold text-gray-900 shadow-lg shadow-sky-500/30 hover:scale-[1.02]"
              >
                Run a LinkedIn sprint
              </a>
              <a
                href="/modules/business-consultant"
                className="inline-flex items-center justify-center rounded-full border border-blue-600/60 px-6 py-3 text-base font-semibold text-gray-600 hover:border-slate-300/70"
              >
                Pair with the Consultant
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Scenarios */}
      <section aria-label="Use cases" className="bg-white py-20">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center">
            <h2 className="text-3xl font-bold text-gray-900">Where teams depend on LinkedIn Enhancer</h2>
            <p className="mt-4 text-base text-gray-500">Accelerate fundraising, recruiting, and outbound with profiles that speak the same language as your campaigns.</p>
          </div>
          <div className="mt-12 grid gap-6 lg:grid-cols-3">
            {[{
              title: 'Founder fundraising prep',
              body: 'Refresh founder bios and traction narratives before roadshows; sync investor follow-ups via the Business Consultant.'
            }, {
              title: 'Hiring sprints',
              body: 'Update company and leadership pages to highlight culture and open roles, then push messaging into outreach cadences.'
            }, {
              title: 'Account-based marketing',
              body: 'Personalize founder and company positioning for target industries, and log the approved copy in CRM playbooks.'
            }].map((scenario) => (
              <article key={scenario.title} className="rounded-3xl border border-gray-200 bg-white p-6 shadow-lg">
                <h3 className="text-xl font-semibold text-gray-900">{scenario.title}</h3>
                <p className="mt-3 text-sm text-gray-500">{scenario.body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section aria-label="FAQs" className="bg-white py-16">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-2xl font-semibold text-gray-900">Frequently asked questions</h2>
          <div className="mt-6 space-y-4">
            {[{
              q: 'What inputs do I need?',
              a: 'For personal profiles: headline, experience highlights, skills, and career goals. For company pages: services, selling points, audience, and values.'
            }, {
              q: 'Will it stay updated as we grow?',
              a: 'Yes—generate new drafts whenever your positioning changes. No per-refresh fees or scheduling delays.'
            }, {
              q: 'Can the AI help with outreach after profiles are updated?',
              a: 'The AI Business Consultant can generate InMail sequences, nurture emails, and meeting prep based on the new messaging.'
            }, {
              q: 'Do I lose access to the original enhancers?',
              a: 'No—the dedicated profile and business enhancers remain available for in-app creation. This landing page unifies the value proposition for marketing and acquisition.'
            }].map((item) => (
              <details key={item.q} className="group rounded-2xl border border-gray-200 bg-white p-5 shadow">
                <summary className="cursor-pointer text-lg font-semibold text-gray-900 group-open:text-sky-700">{item.q}</summary>
                <p className="mt-3 text-sm text-gray-500">{item.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ JSON-LD */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />

      {/* Pricing */}
      <section id="pricing" aria-label="Pricing" className="bg-white py-16">
        <div className="max-w-7xl mx-auto px-4">
          <PricingSection />
        </div>
      </section>
    </main>
  );
};

export default LinkedInEnhancerLanding;
